import { h } from 'preact';
import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { api } from '../api/client';
import { Button } from '../components/ui';
import { T } from '../styles/tokens';

interface Runner {
  id: string;
  customer_id: string;
  customer_email?: string;
  name: string;
  status: string;
  type: string;
  os: string;
  arch: string;
  version: string;
  mode: string;
  ip: string;
  agent_name: string;
  agent_model: string;
  machine_id: string;
  capabilities: string;
  pending_config: string;
  openclaw_path: string;
  openclaw_version: string;
  last_seen_at: string;
  created_at: string;
  updated_at: string;
  cloud_machine_id?: string;
  machine_name?: string;
  machine_display_name?: string;
  machine_status?: string;
  machine_ip?: string;
  machine_provider?: string;
  plan_name?: string;
  plan_cpus?: number;
  plan_memory_gb?: number;
  plan_storage_gb?: number;
  plan_price_usd?: number;
  [key: string]: any;
}

const COLORS = {
  bg: T.bg,
  card: T.bgInput,
  border: T.border,
  text: T.text,
  muted: T.textSecondary,
  dimmed: T.textMuted,
  primary: T.primary,
  success: T.success,
  warning: T.warning,
  error: T.danger,
};

const TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  cloud: { bg: T.primaryBg, text: '#5B8FFF' },
  mac: { bg: T.purpleBg, text: '#C084FC' },
  linux: { bg: T.successBg, text: T.success },
  windows: { bg: T.primaryBg, text: '#22D3EE' },
};

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Nunca';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Agora';
  if (mins < 60) return `${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function StatusDot({ status }: { status: string }) {
  const color = status === 'online' ? COLORS.success : status === 'disabled' ? COLORS.error : COLORS.dimmed;
  return (
    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: color, boxShadow: status === 'online' ? `0 0 8px ${COLORS.success}` : 'none' }} />
  );
}

function TypeBadge({ type }: { type: string }) {
  const t = (type || '').toLowerCase();
  const c = TYPE_COLORS[t] || { bg: T.borderHover, text: COLORS.dimmed };
  return (
    <span style={{ padding: '2px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: c.bg, color: c.text, textTransform: 'capitalize' as const }}>
      {type || 'N/A'}
    </span>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ flex: 1, background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: '20px 24px' }}>
      <div style={{ fontSize: 13, color: COLORS.muted, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color, fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div>
      {[...Array(6)].map((_, i) => (
        <div key={i} style={{ height: 52, background: i % 2 === 0 ? T.bgOverlay : 'transparent', borderRadius: 8, marginBottom: 2 }}>
          <div style={{ height: 14, width: `${60 + Math.random() * 30}%`, background: 'rgba(30,41,59,0.6)', borderRadius: 6, margin: '19px 16px' }} />
        </div>
      ))}
    </div>
  );
}

function DetailPanel({ runner }: { runner: Runner }) {
  const fields = [
    ['ID', runner.id],
    ['Customer ID', runner.customer_id],
    ['Email', runner.customer_email],
    ['Nome', runner.name],
    ['Status', runner.status],
    ['Tipo', runner.type],
    ['SO / Arch', `${runner.os || 'N/A'} / ${runner.arch || 'N/A'}`],
    ['Versao', runner.version],
    ['Modo', runner.mode],
    ['IP', runner.ip],
    ['Agente', runner.agent_name],
    ['Modelo', runner.agent_model],
    ['Machine ID', runner.machine_id],
    ['Maquina', runner.machine_display_name || runner.machine_name || 'Nenhuma'],
    ['Status Maquina', runner.machine_status || 'N/A'],
    ['IP Maquina', runner.machine_ip || 'N/A'],
    ['Provedor', runner.machine_provider || 'N/A'],
    ['Plano', runner.plan_name || 'Nenhum'],
    ['Plano Specs', runner.plan_cpus ? `${runner.plan_cpus} vCPU / ${runner.plan_memory_gb}GB RAM / ${runner.plan_storage_gb}GB Disco` : 'N/A'],
    ['Preco Plano', runner.plan_price_usd != null ? `$${runner.plan_price_usd}` : 'N/A'],
    ['Capabilities', runner.capabilities],
    ['Pending Config', runner.pending_config],
    ['OpenClaw Path', runner.openclaw_path],
    ['OpenClaw Version', runner.openclaw_version],
    ['Ultimo Acesso', runner.last_seen_at || 'Nunca'],
    ['Criado em', runner.created_at],
    ['Atualizado em', runner.updated_at],
  ];

  return (
    <div style={{ background: 'rgba(17,24,39,0.8)', border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 20, margin: '4px 0 8px 0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 32px' }}>
        {fields.map(([label, value]) => (
          <div key={label}>
            <span style={{ fontSize: 12, color: COLORS.dimmed }}>{label}</span>
            <div style={{ fontSize: 13, color: COLORS.text, fontFamily: "'JetBrains Mono', monospace", wordBreak: 'break-all' as const }}>
              {value || 'N/A'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminRunnersPage() {
  const [runners, setRunners] = useState<Runner[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const intervalRef = useRef<any>(null);

  const fetchRunners = useCallback(async () => {
    try {
      const res = await api.get<{ ok: boolean; runners: Runner[] }>('/admin/runners');
      setRunners(res.runners || []);
    } catch (e) {
      console.error('Failed to fetch runners', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRunners();
    intervalRef.current = setInterval(fetchRunners, 15000);
    return () => clearInterval(intervalRef.current);
  }, [fetchRunners]);

  const handleToggle = async (id: string, e: Event) => {
    e.stopPropagation();
    await api.patch<{ ok: boolean; status: string }>(`/admin/runners/${id}/toggle`, {});
    fetchRunners();
  };

  const handleDelete = async (id: string, e: Event) => {
    e.stopPropagation();
    if (confirmDelete !== id) {
      setConfirmDelete(id);
      return;
    }
    await api.delete(`/admin/runners/${id}`);
    setConfirmDelete(null);
    fetchRunners();
  };

  const filtered = runners.filter((r) => {
    if (search) {
      const q = search.toLowerCase();
      if (!(r.name || '').toLowerCase().includes(q) && !(r.customer_email || '').toLowerCase().includes(q)) return false;
    }
    if (typeFilter !== 'all' && (r.runner_type || r.type || '').toLowerCase() !== typeFilter) return false;
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    return true;
  });

  const stats = {
    total: runners.length,
    online: runners.filter((r) => r.status === 'online').length,
    offline: runners.filter((r) => r.status === 'offline').length,
    disabled: runners.filter((r) => r.status === 'disabled').length,
  };

  const selectStyle: any = {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    padding: '8px 14px',
    color: COLORS.text,
    fontSize: 13,
    outline: 'none',
    fontFamily: "'Manrope', system-ui",
  };

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1400, margin: '0 auto', fontFamily: "'Manrope', system-ui", color: COLORS.text }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Runners</h1>
        <p style={{ fontSize: 14, color: COLORS.muted, marginTop: 4 }}>Gerenciamento de todos os runners do sistema</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total" value={stats.total} color={COLORS.text} />
        <StatCard label="Online" value={stats.online} color={COLORS.success} />
        <StatCard label="Offline" value={stats.offline} color={COLORS.dimmed} />
        <StatCard label="Desabilitados" value={stats.disabled} color={COLORS.error} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' as const }}>
        <input
          type="text"
          placeholder="Buscar por nome ou email..."
          value={search}
          onInput={(e: any) => setSearch(e.target.value)}
          style={{ ...selectStyle, flex: 1, minWidth: 220 }}
        />
        <select value={typeFilter} onChange={(e: any) => setTypeFilter(e.target.value)} style={selectStyle}>
          <option value="all">Todos os tipos</option>
          <option value="cloud">Cloud</option>
          <option value="mac">Mac</option>
          <option value="linux">Linux</option>
          <option value="windows">Windows</option>
        </select>
        <select value={statusFilter} onChange={(e: any) => setStatusFilter(e.target.value)} style={selectStyle}>
          <option value="all">Todos os status</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="disabled">Desabilitado</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, overflow: 'hidden' }}>
        {/* Header row */}
        <div style={{ display: 'grid', gridTemplateColumns: '40px 1.2fr 1.2fr 1.2fr 90px 100px 80px 90px 90px 140px', padding: '14px 16px', borderBottom: `1px solid ${COLORS.border}`, fontSize: 12, fontWeight: 600, color: COLORS.dimmed, textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>
          <span />
          <span>Nome</span>
          <span>Cliente</span>
          <span>Maquina / Plano</span>
          <span>Tipo</span>
          <span>SO/Arch</span>
          <span>Versao</span>
          <span>Visto</span>
          <span>OpenClaw</span>
          <span style={{ textAlign: 'right' as const }}>Acoes</span>
        </div>

        {loading ? (
          <SkeletonRows />
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' as const, color: COLORS.dimmed }}>Nenhum runner encontrado</div>
        ) : (
          filtered.map((r) => (
            <div key={r.id}>
              <div
                onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '40px 1.2fr 1.2fr 1.2fr 90px 100px 80px 90px 90px 140px',
                  padding: '12px 16px',
                  alignItems: 'center',
                  cursor: 'pointer',
                  borderBottom: `1px solid rgba(30,41,59,0.4)`,
                  fontSize: 13,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e: any) => (e.currentTarget.style.background = T.primaryBg)}
                onMouseLeave={(e: any) => (e.currentTarget.style.background = 'transparent')}
              >
                <span style={{ display: 'flex', justifyContent: 'center' }}><StatusDot status={r.status} /></span>
                <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{r.name || 'Sem nome'}</span>
                <span style={{ color: COLORS.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{r.customer_email || r.customer_id?.slice(0, 8) || 'N/A'}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                  {r.machine_display_name || r.machine_name ? (
                    <span style={{ fontSize: 12 }}>
                      <span style={{ color: COLORS.text, fontWeight: 500 }}>{r.machine_display_name || r.machine_name}</span>
                      {r.plan_name && <span style={{ color: COLORS.dimmed }}>{' / '}{r.plan_name}</span>}
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, color: COLORS.dimmed }}>Sem maquina</span>
                  )}
                </span>
                <span><TypeBadge type={r.runner_type || r.type} /></span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: COLORS.muted }}>{r.os || '-'}/{r.arch || '-'}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: COLORS.muted }}>{r.version || '-'}</span>
                <span style={{ fontSize: 12, color: COLORS.muted }}>{timeAgo(r.last_seen_at)}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: COLORS.dimmed }}>{r.openclaw_path ? 'Sim' : '-'}</span>
                <span style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Button
                    variant={r.status === 'disabled' ? 'success' : 'ghost'}
                    size="xs"
                    onClick={(e: any) => handleToggle(r.id, e)}
                  >
                    {r.status === 'disabled' ? 'Ativar' : 'Desativar'}
                  </Button>
                  <Button
                    variant="danger"
                    size="xs"
                    onClick={(e: any) => handleDelete(r.id, e)}
                  >
                    {confirmDelete === r.id ? 'Confirmar' : 'Excluir'}
                  </Button>
                </span>
              </div>
              {expandedId === r.id && <div style={{ padding: '0 16px 12px' }}><DetailPanel runner={r} /></div>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
